import React, { useState } from "react";
import { Button, Input, message } from "antd";
import aiConsoleService from "services/aiconsoleservice";

// antd v5 primary buttons render transparent under Tailwind Preflight; force the themed fill.
const PRIMARY_BTN = "!bg-[#0b1437] hover:!bg-[#1e293b] !border-[#0b1437] !text-white";

const Field = ({ label, hint, children }) => (
  <div className="py-3 border-b border-gray-100 dark:border-gray-700 last:border-0">
    <label className="block text-sm font-medium text-slate-700 dark:text-gray-200 mb-1">{label}</label>
    {children}
    {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
  </div>
);

// Step 1 of building an agent: define its role and goal. This is the human-authored
// identity block, kept separate from the system Prompt (edited in the Prompts tab).
const RoleGoalForm = ({ agent, onSaved }) => {
  const editable = agent?.status === "editable";
  const def = agent?.role_definition ?? {};
  const [form, setForm] = useState({
    role: def.role ?? "",
    goal: def.goal ?? "",
    audience: def.audience ?? "",
    output: def.output ?? "",
  });
  const [saving, setSaving] = useState(false);
  const [reason, setReason] = useState("");

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const isEmpty = !def.role && !def.goal && !def.audience && !def.output;

  const save = async () => {
    setSaving(true);
    try {
      const res = await aiConsoleService.updateAgentRole(agent.agent_key, { ...form, reason });
      message.success("Role and goal saved.");
      setReason("");
      onSaved?.(res?.data?.settings);
    } catch (e) {
      message.error("Could not save the role and goal.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="agent-role-form" className="max-w-2xl">
      <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">
        Step 1 of building an agent: define what it does, who it helps, and what it produces.
        This is the agent's role and goal, separate from the system Prompt.
      </p>
      {!editable && (
        <div className="mb-3 text-sm text-gray-400 dark:text-gray-500">
          This agent is managed in a later release. Its role is read-only.
        </div>
      )}
      {editable && isEmpty && (
        <div className="mb-3 text-sm text-amber-600 dark:text-amber-400">
          No role defined yet. Fill in the fields below to describe this agent.
        </div>
      )}
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
        <Field label="Role" hint="What is this agent, in a few words? e.g. HR policy assistant.">
          <Input
            data-testid="agent-role"
            disabled={!editable}
            value={form.role}
            maxLength={255}
            onChange={(e) => set("role", e.target.value)}
          />
        </Field>
        <Field label="Goal" hint="What will this agent do?">
          <Input.TextArea
            data-testid="agent-goal"
            rows={3}
            disabled={!editable}
            value={form.goal}
            maxLength={2000}
            onChange={(e) => set("goal", e.target.value)}
          />
        </Field>
        <Field label="Who it helps" hint="Who is it helping?">
          <Input.TextArea
            data-testid="agent-audience"
            rows={2}
            disabled={!editable}
            value={form.audience}
            maxLength={1000}
            onChange={(e) => set("audience", e.target.value)}
          />
        </Field>
        <Field label="What it produces" hint="What kind of output will it generate?">
          <Input.TextArea
            data-testid="agent-output"
            rows={3}
            disabled={!editable}
            value={form.output}
            maxLength={2000}
            onChange={(e) => set("output", e.target.value)}
          />
        </Field>
        {editable && (
          <Field label="Reason for change" hint="Optional. Briefly say why (for the audit trail).">
            <Input.TextArea
              data-testid="agent-role-reason"
              rows={2}
              value={reason}
              maxLength={500}
              onChange={(e) => setReason(e.target.value)}
            />
          </Field>
        )}
      </div>
      {editable && (
        <Button
          data-testid="agent-role-save"
          type="primary"
          className={`mt-4 ${PRIMARY_BTN}`}
          loading={saving}
          onClick={save}
        >
          Save role and goal
        </Button>
      )}
    </div>
  );
};

export default RoleGoalForm;
